import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import { IconButton, Popover, makeStyles, useTheme } from '@material-ui/core';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import { DateField, DateFieldProps } from '@mui/x-date-pickers/DateField';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import {
  MouseEvent,
  forwardRef,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { Calendar } from 'react-date-range';
import { DateTime } from 'luxon';
import { enGB } from 'date-fns/locale';
import {
  AccessibilityProps,
  WbTextField,
  WbTextFieldProps,
} from './WbTextField';
import { ClearButton } from './ClearButton';

export type WbDatePickerProps = Omit<
  WbTextFieldProps,
  'type' | 'onChange' | 'value' | 'inputProps' | 'SelectProps' | 'InputProps'
> &
  AccessibilityProps & {
    value?: string;
    onChange: (value?: string) => void;
    id?: string;
    ariaLabel?: string;
    label?: string | React.ReactNode;
    minDate?: Date;
    maxDate?: Date;
    closeOnSelect?: boolean;
    dateFormat?: string;
    displayFormat?: string;
    allowKeyboardInput?: boolean;
  };

const useStyles = makeStyles(
  theme => ({
    modalRoot: {
      background: theme.palette.background.default,
      fontFamily: theme.typography.fontFamily,
      '--font-family': theme.typography.fontFamily as unknown as string,
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(1),
    },
  }),
  { name: 'WbDatePicker' },
);

const parseDate = (value: string, format: string): DateTime =>
  DateTime.fromFormat(value, format, { locale: 'en' });

const DateFieldTextField = forwardRef<
  HTMLDivElement,
  WbTextFieldProps & { ownerState?: DateFieldProps<DateTime> }
>(({ ownerState: _ownerState, ...props }, ref) => (
  <WbTextField {...props} ref={ref} />
));

export const WbDatePicker = forwardRef<HTMLDivElement, WbDatePickerProps>(
  (props, ref) => {
    const {
      value,
      onChange,
      label,
      id,
      ariaLabel,
      disabled,
      minDate,
      maxDate,
      placeholder,
      fullWidth,
      closeOnSelect = true,
      dateFormat = 'yyyy/MM/dd',
      displayFormat = dateFormat,
      allowKeyboardInput = false,
      helperText,
      error,
      style,
      ...rest
    } = props;

    const classes = useStyles();
    const theme = useTheme();

    const [anchorEl, setAnchorEl] = useState<Element | null>(null);
    const [selectedDate, setSelectedDate] = useState<DateTime | null>(() =>
      value ? parseDate(value, dateFormat) : null,
    );

    useEffect(() => {
      setSelectedDate(value ? parseDate(value, dateFormat) : null);
    }, [value, dateFormat]);

    const handleIconClick = useCallback(
      (e: MouseEvent<HTMLButtonElement>) => {
        if (disabled) return;
        setAnchorEl(e.currentTarget);
      },
      [disabled],
    );

    const handleInputClick = useCallback(
      (e: MouseEvent<HTMLInputElement>) => {
        if (disabled) return;
        setAnchorEl(e.currentTarget);
      },
      [disabled],
    );

    const open = Boolean(anchorEl);

    const handleClose = useCallback(() => setAnchorEl(null), []);

    const handleSelect = useCallback(
      (date: Date) => {
        const formatted = DateTime.fromJSDate(date).toFormat(dateFormat, {
          locale: 'en',
        });
        setSelectedDate(DateTime.fromJSDate(date));
        onChange?.(formatted);
        if (closeOnSelect) handleClose();
      },
      [closeOnSelect, handleClose, onChange, dateFormat],
    );

    const handleInputChange = (date: DateTime | null) => {
      setSelectedDate(date);
      onChange(date ? date.toFormat(dateFormat, { locale: 'en' }) : undefined);
    };

    const accessibilityProps = useMemo(() => {
      return id && label
        ? { id, label }
        : { id, ariaLabel: ariaLabel ?? 'select date' };
    }, [ariaLabel, id, label]);

    const textFieldProps = {
      ref,
      fullWidth,
      style,
      placeholder,
      error,
      helperText,
      disabled,
      InputProps: {
        endAdornment: (
          <>
            {value && !disabled && (
              <ClearButton
                style={{
                  visibility: 'visible',
                  marginLeft: theme.spacing(0.5),
                  pointerEvents: 'initial',
                }}
                onClear={() => {
                  onChange(undefined);
                  handleClose();
                }}
              />
            )}
            <IconButton
              aria-label="open calendar"
              onClick={handleIconClick}
              size="small"
              style={{ marginLeft: theme.spacing(0.5) }}
              disabled={disabled}
            >
              <CalendarTodayIcon fontSize="small" />
            </IconButton>
          </>
        ),
      },
      InputLabelProps: { shrink: true },
      ...rest,
      ...accessibilityProps,
    };

    return (
      <>
        {allowKeyboardInput ? (
          <LocalizationProvider
            dateAdapter={AdapterLuxon}
            adapterLocale="en-GB"
          >
            <DateField<DateTime>
              ref={ref}
              slotProps={{ textField: textFieldProps }}
              value={selectedDate}
              format={displayFormat}
              onChange={handleInputChange}
              minDate={
                minDate
                  ? DateTime.fromJSDate(minDate)
                  : DateTime.fromISO('0001-01-01')
              }
              maxDate={
                maxDate
                  ? DateTime.fromJSDate(maxDate)
                  : DateTime.fromISO('9999-12-31')
              }
              slots={{ textField: DateFieldTextField }}
            />
          </LocalizationProvider>
        ) : (
          <WbTextField
            {...textFieldProps}
            value={
              selectedDate?.isValid
                ? selectedDate.toFormat(displayFormat, { locale: 'en' })
                : value ?? ''
            }
            inputProps={{ readOnly: true, onClick: handleInputClick }}
          />
        )}
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <div className={classes.modalRoot}>
            <Calendar
              locale={enGB}
              date={
                selectedDate?.isValid ? selectedDate.toJSDate() : new Date()
              }
              onChange={item => handleSelect(item as Date)}
              color={theme.palette.primary.main}
              minDate={minDate}
              maxDate={maxDate}
            />
          </div>
        </Popover>
      </>
    );
  },
);

WbDatePicker.displayName = 'WbDatePicker';
